import {
  LabraChatLayout,
  LabraChatList,
  LabraChatMessage,
  LabraChatMessageDocument,
  LabraChatEmailPreview,
  LabraChatState,
  NextBestAction,
} from './reducer'
import { UserProfileState } from '../userProfile/reducer'
import { SavedPrompt } from './promptsApi'

/**
 * Minimal state shape the chat selectors + components need. Any store that
 * mounts the common reducers (the host root store, or the funding standalone
 * harness) satisfies this structurally — the slices stay optional because they
 * may not be registered when a selector first runs. `userProfile` is read by
 * the user bubble for the sender's initials.
 */
export interface LabraChatSliceState {
  labraChat?: LabraChatState
  userProfile?: UserProfileState
}

/**
 * Selectors for the `labraChat` slice. The slice is optional (it can be absent
 * before the reducer mounts), so every selector applies the `?.` + default here
 * rather than repeating that boilerplate at each call site.
 */
export const selectLabraChatMessages = (
  state: LabraChatSliceState
): LabraChatMessage[] => state.labraChat?.messages ?? []

export const selectLabraChatSessionId = (state: LabraChatSliceState): string =>
  state.labraChat?.sessionId ?? ''

export const selectLabraChatLayout = (
  state: LabraChatSliceState
): LabraChatLayout | null => state.labraChat?.layout ?? null

export const selectLabraChatOpenList = (
  state: LabraChatSliceState
): LabraChatList | null => state.labraChat?.openList ?? null

export const selectLabraChatListOrigin = (
  state: LabraChatSliceState
): LabraChatList | null => state.labraChat?.listOrigin ?? null

export const selectLabraChatSavedPrompts = (
  state: LabraChatSliceState
): SavedPrompt[] => state.labraChat?.savedPrompts ?? []

export const selectIsLabraChatLoading = (state: LabraChatSliceState): boolean =>
  state.labraChat?.isLoading ?? false

export const selectIsLabraChatPanelOpen = (
  state: LabraChatSliceState
): boolean => state.labraChat?.isPanelOpen ?? false

export const selectIsLabraChatFullScreen = (
  state: LabraChatSliceState
): boolean => state.labraChat?.isFullScreen ?? false

export const selectIsLabraChatHistoryOpen = (
  state: LabraChatSliceState
): boolean => state.labraChat?.isHistoryOpen ?? false

export const selectLabraChatPreviewDocument = (
  state: LabraChatSliceState
): LabraChatMessageDocument | null => state.labraChat?.previewDocument ?? null

export const selectLabraChatPreviewEmail = (
  state: LabraChatSliceState
): LabraChatEmailPreview | null => state.labraChat?.previewEmail ?? null

export const selectLabraChatNextBestActions = (
  state: LabraChatSliceState
): NextBestAction[] => state.labraChat?.nextBestActions ?? []

export const selectIsLabraChatGeneratingNextActions = (
  state: LabraChatSliceState
): boolean => state.labraChat?.isGeneratingNextActions ?? false

export const selectLabraChatComposerDraft = (
  state: LabraChatSliceState
): string | null => state.labraChat?.composerDraft ?? null
